import { GitBranch, FileText, Image, CalendarCheck, Users, ArrowRightLeft } from "lucide-react";
import { useScrollAnimation } from "@/hooks/useScrollAnimation";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";

import visuellerBuilderImg from "@/assets/flow-features/visueller-builder.png";
import formularImg from "@/assets/flow-features/formular-einbindung.png";
import richMediaImg from "@/assets/flow-features/rich-media.png";
import terminImg from "@/assets/flow-features/terminbuchungen.png";
import leadRoutingImg from "@/assets/flow-features/lead-routing.png";
import datenImg from "@/assets/flow-features/datenuebermittlung.png";

const FlowBuilderFunktionenSection = () => {
  const { ref, isVisible } = useScrollAnimation();

  const features = [
    {
      icon: GitBranch,
      title: "Visueller Flow-Builder",
      description: "Automation Flows einfach per Drag & Drop erstellen – ohne Programmierkenntnisse.",
      image: visuellerBuilderImg,
    },
    {
      icon: FileText,
      title: "Formular-Einbindung",
      description: "Kundeninformationen strukturiert im Dialog abfragen und direkt verarbeiten.",
      image: formularImg,
    },
    {
      icon: Image,
      title: "Rich Media und Dokumente",
      description: "Bilder, Videos, Karussells und PDFs direkt im Chat bereitstellen – von Produktinfos bis Angebotsdokumente.",
      image: richMediaImg,
    },
    {
      icon: CalendarCheck,
      title: "Terminbuchungen",
      description: "Termine direkt im Chat vereinbaren – ohne Medienbruch.",
      image: terminImg,
    },
    {
      icon: Users,
      title: "Aktives Lead-Routing",
      description: "Qualifizierte Leads automatisch an das richtige Vertriebsteam weiterleiten.",
      image: leadRoutingImg,
    },
    {
      icon: ArrowRightLeft,
      title: "Datenübermittlung an Systeme",
      description: "Erfasste Daten automatisch an HubSpot, CRM- oder Ticketsysteme übergeben.",
      image: datenImg,
    },
  ];

  return (
    <section
      ref={ref as React.RefObject<HTMLElement>}
      className={cn(
        "py-16 md:py-24 bg-secondary/30 transition-all duration-700 ease-out",
        isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
      )}
    >
      <div className="container mx-auto px-4">
        <div className="max-w-5xl mx-auto">
          <div className="text-center">
            <Badge variant="outline" className="mb-4 text-sm font-semibold text-primary uppercase tracking-wider">Agent-Flow-Builder & Funktionen</Badge>

            <h2 className="text-3xl md:text-4xl font-semibold text-foreground mb-6">
              Was AI Workflows konkret leisten
            </h2>
          </div>

          <p className="text-xl text-muted-foreground text-center mb-12 max-w-3xl mx-auto">
            Erstellen Sie Automation Flows visuell – mit Funktionen, die Dialoge in messbare Ergebnisse verwandeln.
          </p>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <div
                key={index}
                className={cn(
                  "bg-card rounded-2xl border border-border transition-all hover:shadow-md overflow-hidden",
                  isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
                )}
                style={{ transitionDelay: isVisible ? `${index * 80}ms` : "0ms", transitionDuration: "500ms" }}
              >
                <div className="aspect-[5/3] bg-secondary/50 overflow-hidden">
                  <img
                    src={feature.image}
                    alt={feature.title}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </div>
                <div className="p-6">
                  <div className="w-10 h-10 rounded-xl bg-chathero-mint/20 flex items-center justify-center mb-4">
                    <feature.icon className="w-5 h-5 text-chathero-mint" />
                  </div>
                  <h3 className="text-lg font-semibold text-primary mb-2">{feature.title}</h3>
                  <p className="text-muted-foreground text-sm">{feature.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default FlowBuilderFunktionenSection;
